import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChatMessage as ChatMessageType } from "@/contexts/ChatContext";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { Loader2, CheckCircle2, XCircle, Eye, RefreshCw, AlertCircle, BarChart3, TrendingUp, Copy, Check } from "lucide-react";
import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ProductPreview } from "./ProductPreview";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const CHART_COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff7300", "#00C49F", "#FFBB28"];

// Safe text formatting without dangerouslySetInnerHTML
function formatTextSafely(text: string): ReactNode {
  const parts: ReactNode[] = [];
  let currentIndex = 0;
  
  // Match bold (**text**) and inline code (`text`)
  const regex = /(\*\*.*?\*\*|`.*?`)/g;
  let match;
  
  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > currentIndex) {
      parts.push(text.slice(currentIndex, match.index));
    }
    
    const matched = match[0];
    if (matched.startsWith("**") && matched.endsWith("**")) {
      // Bold text
      parts.push(<strong key={match.index}>{matched.slice(2, -2)}</strong>);
    } else if (matched.startsWith("`") && matched.endsWith("`")) {
      // Inline code
      parts.push(
        <code key={match.index} className="bg-background/20 px-1 rounded text-xs">
          {matched.slice(1, -1)}
        </code>
      );
    }
    
    currentIndex = match.index + matched.length;
  }
  
  // Add remaining text
  if (currentIndex < text.length) {
    parts.push(text.slice(currentIndex));
  }
  
  return parts.length > 0 ? parts : text;
}

interface ChatMessageProps {
  message: ChatMessageType;
  onPreviewClick?: (product: { name: string; description?: string; short_description?: string; regular_price?: string; sku?: string }) => void;
  isPreviewLoading?: boolean;
  currency?: string;
  onRetry?: (messageId: string) => void;
  onRegenerate?: (messageId: string) => void;
  isRegenerating?: boolean;
}

export function ChatMessage({ message, onPreviewClick, isPreviewLoading, currency, onRetry, onRegenerate, isRegenerating }: ChatMessageProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const isUser = message.role === "user";
  const isThisMessageRegenerating = isRegenerating && message.isTyping;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Could not copy message to clipboard",
        variant: "destructive",
      });
    }
  };

  if (message.isTyping) {
    return (
      <div className="flex justify-start">
        <div className="max-w-[85%] rounded-2xl px-4 py-3 bg-muted text-foreground rounded-bl-md">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm text-muted-foreground">Thinking...</span>
          </div>
        </div>
      </div>
    );
  }

  // Extract pending product from action result
  const actionResult = message.actionResult;
  const pendingProduct =
    actionResult?.toolName === "create_product" && actionResult.success && actionResult.data?.pending
      ? {
          name: actionResult.data.name,
          description: actionResult.data.description,
          short_description: actionResult.data.short_description,
          regular_price: actionResult.data.regular_price,
          sku: actionResult.data.sku,
        }
      : null;

  const handleActionClick = (action: { label: string; type: string }) => {
    const label = (action.label || "").toLowerCase();

    if (label.includes("connect") && label.includes("store")) {
      navigate("/connect-store");
      return;
    }

    if (label.includes("review") && label.includes("confirm")) {
      if (pendingProduct && onPreviewClick) {
        onPreviewClick(pendingProduct);
        return;
      }

      toast({
        title: "No product to review",
        description: "Generate a product, then use “Preview on Your Store”.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Action not available",
      description: `"${action.label}" isn't wired yet.`,
    });
  };

  // Safe actions array
  const actions = Array.isArray(message.actions) ? message.actions : [];

  return (
    <div className={cn("flex", isUser ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[85%] rounded-2xl px-4 py-3",
          isUser
            ? "bg-primary text-primary-foreground rounded-br-md"
            : "bg-muted text-foreground rounded-bl-md"
        )}
      >
        {/* Attached Images for USER messages */}
        {isUser && message.images && message.images.length > 0 && (
          <div className="grid grid-cols-2 gap-2 mb-3">
            {message.images.map((img, idx) => (
              <div key={idx} className="rounded border border-primary-foreground/20 overflow-hidden">
                <img
                  src={`data:${img.mimeType};base64,${img.base64}`}
                  alt={img.filename}
                  className="w-full h-auto object-cover max-h-32"
                  loading="lazy"
                />
                <div className="text-xs px-2 py-1 bg-primary-foreground/10 truncate">
                  {img.filename}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Message Content with safe markdown rendering */}
        <div className="text-sm whitespace-pre-wrap">
          {message.content.split("\n").map((line, i) => {
            // Blockquotes
            if (line.startsWith("> ")) {
              const text = line.slice(2);
              return (
                <blockquote
                  key={i}
                  className="border-l-2 border-primary/50 pl-3 my-2 italic text-muted-foreground"
                >
                  {formatTextSafely(text)}
                </blockquote>
              );
            }
            // List items
            if (line.startsWith("• ") || line.startsWith("- ") || line.match(/^\d+\.\s/)) {
              const prefix = line.match(/^[•\-\d.]+\s*/)?.[0] || "";
              const text = line.replace(/^[•\-\d.]+\s*/, "");
              return (
                <div key={i} className="flex gap-2 my-0.5">
                  <span>{prefix}</span>
                  <span>{formatTextSafely(text)}</span>
                </div>
              );
            }
            // Tables (simple rendering)
            if (line.startsWith("|")) {
              const cells = line.split("|").filter(Boolean);
              const isHeaderSeparator = cells.every(c => c.trim().match(/^[-:]+$/));
              if (isHeaderSeparator) return null;
              return (
                <div key={i} className="flex text-xs my-0.5">
                  {cells.map((cell, j) => (
                    <span
                      key={j}
                      className={cn(
                        "flex-1 px-2 py-1",
                        i === 0 && "font-semibold border-b border-border"
                      )}
                    >
                      {cell.trim()}
                    </span>
                  ))}
                </div>
              );
            }
            // Regular text
            return (
              <span key={i}>
                {formatTextSafely(line)}
                {i < message.content.split("\n").length - 1 && <br />}
              </span>
            );
          })}
        </div>

        {/* Analytics Chart Visualization */}
        {message.chartType && message.chartData && message.chartData.length > 0 && (
          <div className="mt-3 border-t border-border/30 pt-3">
            <div className="flex items-center gap-1 mb-2">
              <BarChart3 className="h-3 w-3 opacity-70" />
              <p className="text-xs font-medium opacity-70">Visualization</p>
            </div>
            <div className="h-32 w-full">
              <ResponsiveContainer width="100%" height="100%">
                {message.chartType === "bar" ? (
                  <BarChart data={message.chartData}>
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10 }}
                      interval={0}
                      angle={-45}
                      textAnchor="end"
                      height={40}
                    />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip
                      formatter={(value: number) => [`$${value.toFixed(2)}`, "Value"]}
                      contentStyle={{ fontSize: 11 }}
                    />
                    <Bar dataKey="value" fill="#8884d8" radius={[4, 4, 0, 0]} />
                  </BarChart>
                ) : message.chartType === "line" ? (
                  <LineChart data={message.chartData}>
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 11 }} />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#8884d8"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                ) : message.chartType === "pie" ? (
                  <PieChart>
                    <Pie
                      data={message.chartData}
                      dataKey="value"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      outerRadius={50}
                      label={({ label }) => label.substring(0, 10)}
                      labelLine={false}
                    >
                      {message.chartData.map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 11 }} />
                  </PieChart>
                ) : null}
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Analytics Breakdown */}
        {message.breakdown && message.breakdown.length > 0 && (
          <div className="mt-3 border-t border-border/30 pt-2">
            <p className="text-xs font-medium mb-1 opacity-70">Breakdown</p>
            <div className="text-xs space-y-1">
              {message.breakdown.slice(0, 5).map((item, i) => (
                <div key={i} className="flex justify-between">
                  <span className="truncate max-w-[60%]">{item.label}</span>
                  <span className="font-medium">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Analytics Context Badge */}
        {message.analyticsContext && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <Badge variant="secondary" className="text-[10px]">
              {message.analyticsContext.ordersAnalyzed} orders analyzed
            </Badge>
            {message.analyticsContext.codeExecuted && (
              <Badge variant="outline" className="text-[10px]">
                <TrendingUp className="h-2 w-2 mr-1" />
                AI Analysis
              </Badge>
            )}
          </div>
        )}

        {/* Copy & Regenerate buttons for assistant messages */}
        {!isUser && !message.isTyping && (
          <div className="flex items-center gap-1 mt-3 pt-2 border-t border-border/20">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={handleCopy}
              disabled={isRegenerating}
            >
              {copied ? (
                <>
                  <Check className="h-3 w-3 mr-1 text-green-500" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-3 w-3 mr-1" />
                  Copy
                </>
              )}
            </Button>
            {onRegenerate && message.id && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => onRegenerate(message.id!)}
                disabled={isRegenerating}
              >
                {isRegenerating ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Regenerating…
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Regenerate
                  </>
                )}
              </Button>
            )}
          </div>
        )}

        {/* Product Preview for created products */}
        {message.actionResult?.toolName === 'create_product' && 
         message.actionResult.success && 
         message.actionResult.data?.product && (
          <ProductPreview
            product={message.actionResult.data.product}
            action="created"
            currency={currency}
          />
        )}

        {/* Action Result Indicator for non-product actions */}
        {message.actionResult && !(message.actionResult.toolName === 'create_product' && message.actionResult.success) && (
          <div className={cn(
            "flex items-start gap-2 mt-3 pt-3 border-t border-border/20 text-xs",
            message.actionResult.success ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
          )}>
            {message.actionResult.success ? (
              <>
                <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  {message.actionResult.toolName === 'list_products' && (
                    <div>
                      <div className="font-medium">Found {message.actionResult.data?.count || 0} products</div>
                    </div>
                  )}
                  {message.actionResult.toolName === 'get_store_stats' && (
                    <div>
                      <div className="font-medium">Store Statistics Retrieved</div>
                      <div className="text-muted-foreground mt-1">
                        {message.actionResult.data?.total_products} products • Health Score: {message.actionResult.data?.health_score}/100
                      </div>
                    </div>
                  )}
                  {!['list_products', 'get_store_stats'].includes(message.actionResult.toolName || '') && (
                    <div className="font-medium">Action completed successfully</div>
                  )}
                </div>
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-medium">
                    {message.actionResult.error || "Action failed"}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* One-Click Preview Button for pending products */}
        {pendingProduct && onPreviewClick && (
          <div className="mt-3 pt-3 border-t border-border/20">
            <Button
              size="sm"
              onClick={() => onPreviewClick(pendingProduct)}
              disabled={isPreviewLoading}
              className="w-full"
            >
              {isPreviewLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating Preview...
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  Preview on Your Store
                </>
              )}
            </Button>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Opens in a new tab. Approve or reject below.
            </p>
          </div>
        )}

        {/* Failed Message Indicator with Retry */}
        {message.failed && isUser && (
          <div className="mt-3 pt-3 border-t border-destructive/20">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-destructive">
                  {message.errorDetails?.title || "Message failed to send"}
                </div>
                <div className="text-xs text-destructive/80 mt-0.5">
                  {message.errorDetails?.description || "An error occurred. Please try again."}
                </div>
                {message.retryCount && message.retryCount > 0 && (
                  <div className="text-xs text-muted-foreground mt-1">
                    Attempt {message.retryCount} of 3
                  </div>
                )}
              </div>
              {message.errorDetails?.canRetry && (message.retryCount || 0) < 3 && onRetry && (
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0 text-xs h-7 border-destructive/30 hover:bg-destructive/10"
                  onClick={() => onRetry(message.id!)}
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Retry
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {actions.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-border/20">
            {actions.map((action, i) => (
              <Button
                key={`action-${i}-${action.label}`}
                size="sm"
                type="button"
                variant={action.type === "outline" ? "outline" : "default"}
                disabled={
                  !!isPreviewLoading &&
                  (action.label || "").toLowerCase().includes("review") &&
                  (action.label || "").toLowerCase().includes("confirm")
                }
                onClick={() => handleActionClick(action)}
                className={cn(
                  "text-xs h-7",
                  action.type !== "outline" && !isUser && "bg-primary text-primary-foreground hover:bg-primary/90"
                )}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
